import dayjs from 'dayjs';
import type { Duration, DurationUnitType } from 'dayjs/plugin/duration';
import { useEffect, useState } from 'react';
import type { Scalars } from '../generated/graphql';
import useAppState from './useAppState';

const URGENT_SECONDS = 180;

const isString = (input: unknown): input is string => typeof input === 'string';

interface TimeResolution {
  label: string;
  unit: DurationUnitType;
}

const timeRes: TimeResolution[] = [
  { unit: 'd', label: 'd' },
  { unit: 'h', label: 'h' },
  { unit: 'm', label: 'mins' },
  { unit: 's', label: 's' },
];

export default function useCountdown(
  compareTime: Scalars['TimeStamp'],
  skip = false
) {
  const { currentTime } = useAppState();

  const [prevCalc, setPrevCalc] = useState({ urgent: false, label: '---' });

  useEffect(() => {
    if (skip || !currentTime || !compareTime || !isString(compareTime)) return;

    const countdown: Duration = dayjs.duration(
      dayjs(compareTime).diff(currentTime)
    );

    const asSeconds = countdown.asSeconds();

    const urgent = asSeconds < URGENT_SECONDS;

    const isNegative = asSeconds < 0;

    const label = timeRes
      .reduce<string[]>((acc, next) => {
        if (acc.length >= 2) return acc;

        if (next.unit === 's' && !urgent) return acc;

        const value = Math.abs(countdown.get(next.unit));

        if (value <= 0) return acc;

        return [...acc, `${value} ${next.label}`];
      }, [])
      .join(' ');

    const finalLabel = isNegative ? `-${label}` : label;

    setPrevCalc((prev) => {
      if (prev.urgent === urgent && prev.label === (finalLabel || 'Now'))
        return prev;

      return {
        urgent,
        label: finalLabel || 'Now',
      };
    });
  }, [currentTime, compareTime, skip]);

  return prevCalc;
}
